import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Calendar, Clock, Zap, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function TrialStartedPage({ onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  const analyticsFired = useRef(false);

  // Get data from location.state or user context
  const stateData = location.state || {};
  const userName = stateData.name || user?.name || 'Student';
  const userEmail = stateData.email || user?.email || '';
  const trialEndsAt = stateData.trialEndsAt || calculateTrialEndDate();

  // Calculate trial end date (14 days from now)
  function calculateTrialEndDate() {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 14);
    return endDate.toISOString();
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calculate days remaining
  const getDaysRemaining = () => {
    const endDate = new Date(trialEndsAt);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Fire analytics on mount
  useEffect(() => {
    if (analyticsFired.current) return;
    analyticsFired.current = true;

    console.log('TrialStartedPage: Firing analytics on /trial-started URL', {
      url: window.location.href,
      pathname: window.location.pathname,
      userName,
      trialEndsAt
    });

    // Fire Meta Pixel Lead/Trial event
    if (window.fbq) {
      window.fbq('track', 'StartTrial', {
        content_name: 'StudyClaw 14-Day Trial',
        trial_end_date: trialEndsAt,
        value: 0,
        currency: 'INR'
      });
      console.log('TrialStartedPage: Meta Pixel StartTrial event fired');
    }

    // Fire Google Analytics event if available
    if (window.gtag) {
      window.gtag('event', 'trial_started', {
        trial_duration: 14,
        trial_end_date: trialEndsAt
      });
      console.log('TrialStartedPage: Google Analytics trial_started event fired');
    }
  }, [trialEndsAt, userName]);

  const handleGoToPlatform = () => {
    onNavigate('dashboard');
  };

  const trialFeatures = [
    'Unlimited AI-powered learning sessions',
    'All subjects and chapters unlocked',
    'Parent dashboard access',
    'Exam readiness tracking',
    'Daily 5-minute practice loops'
  ];

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Animated Sparkles Icon */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            type: "spring", 
            stiffness: 200, 
            damping: 15,
            delay: 0.1 
          }}
          className="flex justify-center mb-8"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500/30 to-purple-500/30 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
            >
              <Sparkles className="w-14 h-14 text-violet-400" strokeWidth={1.5} />
            </motion.div>
          </div>
        </motion.div>

        {/* Welcome Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mb-6"
        >
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-3">
            Welcome, {userName}! 🎉
          </h1>
          <p className="text-slate-400 text-lg">
            Your <span className="text-violet-400 font-semibold">14-day free trial</span> has started.
            <br />Explore everything StudyClaw has to offer!
          </p>
        </motion.div>

        {/* Trial Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-900/50 border-2 border-violet-500/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-violet-400" />
            <h2 className="text-sm font-semibold text-violet-400 uppercase tracking-wide">
              Trial Details
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-violet-400" />
                </div>
                <span className="text-slate-400">Days Remaining</span>
              </div>
              <span className="text-white font-semibold text-lg">{getDaysRemaining()} days</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-slate-400">Trial Ends On</span>
              </div>
              <span className="text-white font-semibold">{formatDate(trialEndsAt)}</span>
            </div>
          </div>
        </motion.div>

        {/* Features List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-900/30 rounded-xl p-4 mb-8"
        >
          <p className="text-sm text-slate-500 mb-3 font-medium">What&apos;s included in your trial:</p>
          <ul className="space-y-2">
            {trialFeatures.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="flex items-center gap-2 text-sm text-slate-300"
              >
                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                {feature}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <button
            onClick={handleGoToPlatform}
            data-testid="go-to-platform-btn"
            className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-4 font-semibold text-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-violet-500/25"
          >
            Go to Platform
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
          className="text-center text-slate-600 text-xs mt-6"
        >
          No credit card required. Cancel anytime.
        </motion.p>
      </div>
    </div>
  );
}
